<template>
    <div id="verification-queue-container">
        <card title="Verification Queue">
            <b-row>
                <b-col cols="7">
                    <b-row>
                        <b-col>
                            Stores:
                            <multiselect v-model="selectedStores" :options="stores" :close-on-select="false"
                                :clear-on-select="false" label="name" track-by="id" multiple/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-form-group>
                                <b-input class="mt-4 py-3" v-model="searchString" autofocus label="Search"
                                    placeholder="Rx, Patient Name, Drug Name" @input="throttledSearch"></b-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <div class="d-flex align-items-center flex-wrap">
                                <b-checkbox v-model="showPending" @input="throttledSearch" inline>
                                    Show Pending
                                    <HelpPopup
                                        text="When checked, all orders waiting for pharmacist verification will automatically load without needing to search by name or Rx number. When unchecked, you must type a search term to see results." />
                                </b-checkbox>
                                <b-checkbox v-model="showFilled" @input="throttledSearch" inline class="ml-3">
                                    Show Filled
                                    <HelpPopup
                                        text="When checked, only shows prescriptions where the medication has already been dispensed and is ready for pharmacist verification. When unchecked, all prescriptions awaiting verification are shown regardless of whether they've been dispensed yet." />
                                </b-checkbox>
                                <b-button variant="dark" @click="clearFilters" class="ml-3" size="sm">
                                    Clear Filters
                                </b-button>
                            </div>
                            <small v-if="showPending && showFilled" class="text-muted d-block mt-1">
                                <i class="fa fa-info-circle mr-1" />Both options checked: automatically loading all dispensed prescriptions awaiting pharmacist verification.
                            </small>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col cols="5">
                    <div>
                        <b-row align-v="center">
                            <b-col cols="8">
                                <b-form-group label="Reprint By Order Id or Store-Rx-Rf" label-class="font-weight-bold">
                                    <b-input v-model="ReprintId" placeholder="Order Id/Store-Rx-Rf"></b-input>
                                </b-form-group>
                            </b-col>
                            <b-col cols="4" class="mt-3">
                                <b-dropdown v-if="showPrinters" size="sm" id="printer-dropdown"
                                    :text="selectedPrinter && selectedPrinter.name">
                                    <b-dropdown-item v-for="printer in printers" :key="`printer-${printer.id}`"
                                        @click="selectPrinter(printer)">{{ printer.name }}
                                    </b-dropdown-item>
                                </b-dropdown>
                                <b-button size="sm" variant="info" @click="reprintLabel"
                                    v-b-tooltip.hover.ds750
                                    title="Reprints the patient bottle Rx labels for the order or prescription identified above. Useful when a label has been lost, damaged, or never printed.">Reprint Labels</b-button>
                            </b-col>
                        </b-row>

                        <b-row align-v="center">
                            <b-col cols="8">
                                <b-form-group label="Void By Tracking Number or Order ID"
                                    label-class="font-weight-bold">
                                    <b-input v-model="voidTrackingNumber" placeholder="Tracking Number or Order ID">
                                    </b-input>
                                </b-form-group>
                            </b-col>
                            <b-col cols="4">
                                <b-button size="sm" class="mt-4" variant="danger" @click="voidShipment">
                                    Void Shipment
                                </b-button>
                            </b-col>
                        </b-row>

                        <div v-if="selectedStores.length" class="mt-2">
                            <div v-if="expressShipdateInfo.length" class="express-shipdate-info mb-2">
                                <div v-for="info in expressShipdateInfo" :key="info.storeId" class="store-shipdate-item">
                                    <strong>{{ info.storeName }}:</strong>
                                    <span v-if="info.isCurrentlyClosed" class="text-warning">
                                         Express Closed (Next: {{ formatDate(info.currentExpressDate) }})
                                    </span>
                                    <span v-else>
                                        <span class="text-success"> Express Open</span>
                                        <span class="text-muted">
                                            → Will set to: {{ formatDate(info.nextExpressDate) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div class="d-flex gap-2">
                                <b-button variant="info" @click="adjustExpressShipdate" size="sm"
                                    v-if="hasOpenStores">
                                    Close Express Shipping
                                </b-button>
                                <b-button variant="success" @click="reopenExpressShipdate" size="sm"
                                    v-if="hasClosedStores">
                                    Reopen Express Shipping
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <OrdersListComponent ref="ordersList" :orders="filteredOrders" mode="verify" :per-page="50" :busy="loading" :hide-pager-when-not-needed="false" @change="loadOrders"/>
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import OrdersListComponent from '@/components/Order/OrdersListComponent.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { Store } from '@/models/Store';
import { Order } from '@/models/Order';
import { Printer } from '@/models/Printer';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { debounce } from 'throttle-debounce';
import { getUserSettingParsed, saveUserSetting } from "@/services/UserSettingsService";

const pendingStorageKey = "verification_queue_showPending";
const filledStorageKey = "verification_queue_showFilled";
const selectedStoresStorageKey = "verification_queue_selectedStores";
const scriptFormat = /\d+-\d+-\d+/;

@Component({
    name: "VerificationQueuePage",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        OrdersListComponent,
        Multiselect,
        HelpPopup,
    },
})
export default class VerificationQueuePage extends Vue {
    @Ref('ordersList') protected readonly ordersList!: OrdersListComponent;

    private orders: Array<Order> = [];
    private searchString: string = "";
    private voidTrackingNumber: string = "";
    private ReprintId: string = "";
    private printers: Array<Printer> = [];
    private selectedPrinter: Printer = {} as Printer;

    protected stores: Array<Store> = [];
    protected selectedStores: Array<Store> = [];
    protected showPending: boolean = false;
    protected showFilled: boolean = false;
    private showPrinters = false;
    private expressShipdateInfo: Array<any> = [];
    private loading: boolean = false;

    async created() {
        this.showPending = this.persistentShowPending;
        this.showFilled = this.persistentShowFilled;
        this.selectedStores = this.persistentSelectedStores;

        const config = { params: { filterByTeam: true } };
        const stores = await Axios.get<Store[]>(`/Store/`, config);
        this.stores = stores.data.map(s => new Store(s));

        // Auto-enable showFilled and select all active stores if coming from dashboard link
        if (this.$route.query.showFilled === 'true') {
            this.showFilled = true;
            this.selectedStores = this.stores.filter(s => s.active);
        }

        if (this.routeStoreId) {
            const routeStore = this.stores.find(s => s.id === this.routeStoreId);
            if (routeStore) this.selectedStores = [routeStore];
        }

        this.loadOrders();
        this.loadPrinters();
        this.loadExpressShipdateInfo();
    }

    mounted() {
        window.scrollTo(0, 0);
    }

    get persistentShowPending(): boolean {
        return getUserSettingParsed<boolean>(pendingStorageKey, false);
    }

    get persistentShowFilled(): boolean {
        return getUserSettingParsed<boolean>(filledStorageKey, false);
    }

    get persistentSelectedStores(): Store[] {
        return getUserSettingParsed<Store[]>(selectedStoresStorageKey, []);
    }

    @Watch('showPending')
    showPendingUpdate(val: any) {
        saveUserSetting(pendingStorageKey, JSON.stringify(val));
    }

    @Watch('showFilled')
    showFilledUpdate(val: any) {
        saveUserSetting(filledStorageKey, JSON.stringify(val));
    }

    @Watch('selectedStores')
    selectedStoresUpdate(val: any) {
        saveUserSetting(selectedStoresStorageKey, JSON.stringify(val));
        this.loadExpressShipdateInfo();
    }

    async loadPrinters() {
        try {
            if (!this.showPrinters) return;
            const response = await Axios.get<Printer[]>('/Printer');
            this.printers = response.data;
            this.selectedPrinter = JSON.parse(localStorage.getItem("CPHubVerifyPrinter") as string) as Printer ?? this.printers[0];
        } catch (error) {
            console.warn(error);
        }
    }

    selectPrinter(printer: Printer) {
        localStorage.setItem("CPHubVerifyPrinter", JSON.stringify(printer));
        this.selectedPrinter = printer;
    }

    get filteredOrders() {
        let orders = this.orders;

        orders = orders.filter(o => this.selectedStores.find(s => s.id === o.storeID));

        return orders;
    }

    get hasOpenStores(): boolean {
        return this.expressShipdateInfo.some(info => !info.isCurrentlyClosed);
    }

    get hasClosedStores(): boolean {
        return this.expressShipdateInfo.some(info => info.isCurrentlyClosed);
    }

    async loadOrders() {
        this.loading = true;
        this.ordersList?.goToFirstPage();
        try {
            const config: AxiosRequestConfig = {
                params: {
                    searchTerm: this.searchString.length ? this.searchString : (this.showPending || this.showFilled) ? '%' : '',
                    showFilled: this.showFilled,
                },
            };
            const response = await Axios.get<Order[]>(`/Order/pending-pv2-search/`, config);

            this.orders = response.data.map(o => new Order(o));

            if (this.orders.length === 1 && this.searchString.match(scriptFormat)) {
                this.$router.push({
                    name: 'OrderVerification',
                    params: { orderId: response.data[0].id.toString() },
                });
            }

            if (this.searchString && !this.orders.length) {
                this.$notification(NotificationOptions.error("No Order Found"));
            }
        } catch (err) {
            console.warn(err);
        } finally {
            this.loading = false;
        }
    }

    private throttledSearch: Function = debounce(250, this.loadOrders);

    get routeStoreId(): number {
        const res = parseInt(this.$route.params.storeId);
        if (isNaN(res)) return 0;
        return res;
    }

    async voidShipment() {
        if (!this.voidTrackingNumber) return;

        try {
            await Axios.post(`/Shipping/VoidLabel/${this.voidTrackingNumber}`);
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully voided shipment!", NotificationOptions.NotificationTypes.success));
        } catch (err) {
            const error = err as AxiosError;
            this.$notification(NotificationOptions.error(err));
            console.log("Failed to void shipment!", { error, response: error?.response });
        }
    }

    async reprintLabel() {
        try {
            const resp = await Axios.get<number>("Order/OrderId", { params: { query: this.ReprintId } });
            const orderId = resp.data;
            this.$router.push({
                name: "OrderVerification",
                params: { orderId: orderId.toString() },
            });
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    clearFilters() {
        this.searchString = "";
        this.selectedStores = [];
        this.showFilled = false;
        this.loadOrders();
    }

    async adjustExpressShipdate() {
        if (!this.selectedStores?.length) return;

        const promises = this.selectedStores.map(async s => {
            try {
                const storeId = s.id;
                await Axios.post(`/Shipping/CloseExpress/${storeId}`);
                return { success: true, storeId };
            } catch (error) {
                const err = error as AxiosError;
                this.$notification(NotificationOptions.error(error));
                console.log("Error while adjusting ship dates", { err, response: err?.response });
                return { success: false, storeId: s.id };
            }
        });

        const results = await Promise.all(promises);
        const successCount = results.filter(r => r.success).length;

        if (successCount > 0) {
            this.$notification(NotificationOptions.notificationOptionsPreset(
                `Ship dates successfully adjusted for ${successCount} store(s)`,
                NotificationOptions.NotificationTypes.success
            ));
        }

        // Reload the shipdate info after adjusting
        await this.loadExpressShipdateInfo();
    }

    async reopenExpressShipdate() {
        if (!this.selectedStores?.length) return;

        const closedStores = this.expressShipdateInfo.filter(info => info.isCurrentlyClosed);
        if (!closedStores.length) return;

        const promises = closedStores.map(async info => {
            try {
                await Axios.post(`/Shipping/ReopenExpress/${info.storeId}`);
                return { success: true, storeId: info.storeId };
            } catch (error) {
                const err = error as AxiosError;
                this.$notification(NotificationOptions.error(error));
                console.log("Error while reopening express shipping", { err, response: err?.response });
                return { success: false, storeId: info.storeId };
            }
        });

        const results = await Promise.all(promises);
        const successCount = results.filter(r => r.success).length;

        if (successCount > 0) {
            this.$notification(NotificationOptions.notificationOptionsPreset(
                `Express shipping reopened for ${successCount} store(s)`,
                NotificationOptions.NotificationTypes.success
            ));
        }

        // Reload the shipdate info after reopening
        await this.loadExpressShipdateInfo();
    }

    async loadExpressShipdateInfo() {
        if (!this.selectedStores?.length) {
            this.expressShipdateInfo = [];
            return;
        }

        try {
            const promises = this.selectedStores.map(s =>
                Axios.get(`/Shipping/ExpressShipDateInfo/${s.id}`)
            );
            const results = await Promise.all(promises);
            this.expressShipdateInfo = results.map(r => r.data);
        } catch (error) {
            console.warn("Error loading express shipdate info", error);
            this.expressShipdateInfo = [];
        }
    }

    formatDate(date: string | null | undefined): string {
        if (!date) return 'N/A';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    }

}

</script>

<style scoped>
.express-shipdate-info {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 10px;
    font-size: 0.9rem;
    max-height: 150px;
    overflow-y: auto;
}

.store-shipdate-item {
    margin-bottom: 5px;
}

.store-shipdate-item:last-child {
    margin-bottom: 0;
}
</style>
